<script setup>
import { onMounted, ref } from 'vue';
import { track as trackServerSide } from 'util/statistics';
import AppIcon from 'module/icon/AppIcon.vue';
import WatchlistButton from 'module/watchlistButton/WatchlistButton.vue';
import ShareModal from 'module/shareModal/ShareModal.vue';
import ApplyButton from 'module/applyButton/ApplyButton.vue';
import { shareApi } from 'util/detect';
import { trackGa4 } from 'util/googleAnalytics';
import { share } from 'util/share';

const props = defineProps({
    isSplitView: Boolean,
    applyAction: {
        type: Object,
        default: () => ({}),
    },
    jobDetail: {
        type: Object,
        default: () => ({}),
    },
    jobHeader: {
        type: Object,
        default: () => ({}),
    },
    isStickyVisible: Boolean,
    shareModal: {
        type: Object,
    },
});

/* Share functionality */
const shareModalOpen = ref(false);
function toggleShareModal() {
    shareModalOpen.value = !shareModalOpen.value;
}
const shareModalTriggerButton = ref(null);
const shareModalClickOutsideIgnoreList = [shareModalTriggerButton];

const subject = 'Job-Empfehlung für dich';
function onShareJob() {
    if (shareApi()) {
        share({
            subject,
            text: decodeURIComponent(window.atob(props.shareModal.jobRecommend.bodyWithoutUrl)),
            jobLink: props.shareModal.jobRecommend.shareLinks.karriere,
            utmParams: '?utm_source=karriere.at&utm_medium=web-share&utm_campaign=job-recommendation',
        });
        trackGa4({
            event: 'share',
            method: 'Web Share',
            content_type: 'job',
            item_id: props.shareModal.jobRecommend.jobId ?? null,
        });
    } else {
        toggleShareModal();
    }
}

/* Print functionality */
function print() {
    window.print?.();
}

onMounted(() => trackServerSide({ name: 'job_view' }));
</script>

<template>
    <div
        class="m-jobHeader"
        :class="{
            'm-jobHeader--splitview': isSplitView,
            'm-jobHeader--standalone': !isSplitView,
            'm-jobHeader--seamless': jobHeader.job.isSeamless,
        }"
    >
        <div class="m-jobHeader__inner">
            <div v-if="applyAction.applyButton.error">
                <div
                    v-if="!jobHeader.job.isInactive"
                    class="m-jobHeader__infoBox"
                >
                    Bitte kontaktiere den Arbeitgeber über den
                    <strong class="m-jobHeader__strong">im Inserat angeführten Kanal</strong>. Eine
                    Online-Bewerbung ist leider nicht möglich.
                </div>
            </div>
            <div
                class="m-jobHeader__container"
                :class="{
                    'm-jobHeader__container--splitview': isSplitView,
                    'm-jobHeader__container--standalone': !isSplitView,
                }"
            >
                <div class="m-jobHeader__info">
                    <div
                        v-if="!isSplitView"
                        class="m-jobHeader__logoContainer"
                    >
                        <img
                            v-if="jobHeader.company.logoUrl"
                            class="m-jobHeader__logo"
                            :alt="jobHeader.company.name"
                            :src="jobHeader.company.logoUrl"
                            width="61"
                            height="61"
                        >
                        <img
                            v-else
                            :alt="jobHeader.company.name"
                            src="/assets/icons/company-placeholder.svg"
                            class="m-jobHeader__logoSvg"
                        >
                    </div>
                    <h1
                        class="m-jobHeader__jobTitle"
                        :class="isSplitView && 'm-jobHeader__jobTitle--isSplitView'"
                    >
                        {{ jobHeader.job.title }}
                    </h1>
                </div>
                <div class="m-jobHeader__actions">
                    <button
                        class="m-jobHeader__action m-jobHeader__print"
                        title="Drucken"
                        :disabled="jobHeader.job.isInactive && !jobHeader.isPreview"
                        data-gtm-element="JOBD: Print Job"
                        @click="print"
                    >
                        <AppIcon
                            name="printer"
                            size="medium"
                        />
                    </button>
                    <div
                        class="m-jobHeader__action m-jobHeader__share"
                        title="Teilen"
                    >
                        <button
                            ref="shareModalTriggerButton"
                            class="m-jobHeader__email"
                            aria-label="Teilen Menü öffnen"
                            :disabled="jobHeader.job.isInactive"
                            @click="onShareJob"
                        >
                            <AppIcon
                                name="share"
                                size="medium"
                                class="m-jobHeader__shareIcon"
                            />
                            <span class="m-jobHeader__emailLabel">Teilen</span>
                        </button>
                        <ShareModal
                            v-model:open="shareModalOpen"
                            :job-recommend="shareModal.jobRecommend"
                            :click-outside-ignore-list="shareModalClickOutsideIgnoreList"
                        />
                    </div>
                    <div class="m-jobHeader__action m-jobHeader__watchlist">
                        <WatchlistButton
                            :job-id="jobHeader.job.id"
                            label="Merken"
                            label-watched="Gemerkt"
                            label-tablet
                            button-style
                            small-button
                            :disabled="jobHeader.job.isInactive"
                            :is-job-header-splitview="isSplitView"
                            :is-fetched="false"
                            :is-watched="false"
                        />
                    </div>
                    <div
                        class="m-jobHeader__action m-jobHeader__apply"
                        :data-ga-event="applyAction.applyButton.clickTrackingEvent"
                    >
                        <ApplyButton
                            v-bind="applyAction.applyButton"
                            :job-id="jobHeader.job.id"
                            :job-bundle-id="jobDetail.jobBundleId"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use "styles";
</style>
